import {
  CreatedIssuesDto,
  GetIssueResponseDtoStatusEnum,
} from '@issuemanager/api-client';
import {
  DEFAULT_FILTER_MODEL_STATE,
  useDataGridContext,
} from '@issuemanager/apps/client/components';

import { useUserContext } from '@issuemanager/apps/client/contexts';
import { useIssueStatuses } from '@issuemanager/client/features/issues';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { CreatedIssuesTypeEnum } from '@issuemanager/constants';
import {
  CreatedIssuesEnum,
  QuickFilterAllEnum,
} from '@issuemanager/test/constants';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickFilterContainer } from './quick-filter-container';
import { QuickFilterItem } from './quick-filter-item';

type ExtendedWorkflowIssues = {
  remediationPlanning?: number;
  inRemediation?: number;
  inProgress?: number;
};

type FilterConfig = {
  type: CreatedIssuesTypeEnum;
  enumValue: CreatedIssuesEnum;
  labelKey: string;
  getCount: (data: CreatedIssuesDto | undefined) => number | undefined;
  condition?: boolean | unknown;
};

export function CreatedIssues({
  createdIssues,
  activeFilter,
  setActiveFilter,
}: {
  createdIssues: CreatedIssuesDto | undefined;
  activeFilter: QuickFilterAllEnum | null;
  setActiveFilter: (value: QuickFilterAllEnum) => void;
}): React.JSX.Element {
  const { t } = useTranslation(['issue']);
  const user = useUserContext();
  const context = useDataGridContext();
  const { issueStatuses, getStatusDisplay } = useIssueStatuses();
  const isDraftIssuesEnabled = useFeatureFlags('draftIssues');
  const isExtendedWorkflowEnabled = useFeatureFlags('issueExtendedWorkflow');

  const statusMap = useMemo(
    () => new Map(issueStatuses.map((status) => [status.statusValue, status])),
    [issueStatuses],
  );

  const statusDraft = statusMap.get(GetIssueResponseDtoStatusEnum.Draft);
  const statusInReview = statusMap.get(GetIssueResponseDtoStatusEnum.Open);
  const statusInProgress = statusMap.get(
    GetIssueResponseDtoStatusEnum.InProgress,
  );

  const clearFilters = (): void => {
    context.setFilterModel(DEFAULT_FILTER_MODEL_STATE);
  };

  const applyCreatorFilter = (): void => {
    context.setColumnVisibilityModel((prevState) => ({
      ...prevState,
      creator: true,
    }));
    context.setFilterModel(
      (prevState) => ({
        ...prevState,
        creator: [user.data],
      }),
      'quick-filter',
    );
  };

  const applyStatusFilter = (
    status: ReturnType<typeof statusMap.get>,
  ): void => {
    if (!status) return;

    context.setColumnVisibilityModel((prevState) => ({
      ...prevState,
      creator: true,
      status: true,
    }));
    context.setFilterModel(
      (prevState) => ({
        ...prevState,
        creator: [user.data],
        status: [status],
      }),
      'quick-filter',
    );
  };

  const applyExtendedWorkflowFilter = (
    status: GetIssueResponseDtoStatusEnum | undefined,
    isInitiated: boolean,
  ): void => {
    if (!status) return;

    const statusConfig = getStatusDisplay({ status, isInitiated });

    if (!statusConfig) return;

    context.setColumnVisibilityModel((prevState) => ({
      ...prevState,
      creator: true,
      status: true,
    }));

    context.setFilterModel(
      (prevState) => ({
        ...prevState,
        creator: [user.data],
        status: [statusConfig],
      }),
      'quick-filter',
    );
  };

  const handleFilterSelection = (type: CreatedIssuesTypeEnum): void => {
    clearFilters();

    switch (type) {
      case CreatedIssuesTypeEnum.ALL:
        applyCreatorFilter();
        break;
      case CreatedIssuesTypeEnum.DRAFT:
        applyStatusFilter(statusDraft);
        break;
      case CreatedIssuesTypeEnum.IN_REVIEW:
        applyStatusFilter(statusInReview);
        break;
      case CreatedIssuesTypeEnum.IN_PROGRESS:
        applyStatusFilter(statusInProgress);
        break;
      case CreatedIssuesTypeEnum.REMEDIATION_PLANNING:
        applyExtendedWorkflowFilter(statusInProgress?.statusValue, false);
        break;
      case CreatedIssuesTypeEnum.IN_REMEDIATION:
        applyExtendedWorkflowFilter(statusInProgress?.statusValue, true);
        break;
    }
  };

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        type: CreatedIssuesTypeEnum.ALL,
        enumValue: CreatedIssuesEnum.ALL,
        labelKey: 'quickFilters.createdIssues.all',
        getCount: (data) => data?.all,
      },
      {
        type: CreatedIssuesTypeEnum.DRAFT,
        enumValue: CreatedIssuesEnum.DRAFT,
        labelKey: 'quickFilters.createdIssues.draft',
        getCount: (data) => data?.draft,
        condition: isDraftIssuesEnabled,
      },
      {
        type: CreatedIssuesTypeEnum.IN_REVIEW,
        enumValue: CreatedIssuesEnum.IN_REVIEW,
        labelKey: isExtendedWorkflowEnabled
          ? 'quickFilters.createdIssues.inReview'
          : 'quickFilters.createdIssues.open',
        getCount: (data) => data?.open,
      },
      ...(isExtendedWorkflowEnabled
        ? [
            {
              type: CreatedIssuesTypeEnum.REMEDIATION_PLANNING,
              enumValue: CreatedIssuesEnum.REMEDIATION_PLANNING,
              labelKey: 'quickFilters.createdIssues.remediationPlanning',
              getCount: () =>
                (createdIssues as ExtendedWorkflowIssues)?.remediationPlanning,
            },
            {
              type: CreatedIssuesTypeEnum.IN_REMEDIATION,
              enumValue: CreatedIssuesEnum.IN_REMEDIATION,
              labelKey: 'quickFilters.createdIssues.inRemediation',
              getCount: () =>
                (createdIssues as ExtendedWorkflowIssues)?.inRemediation,
            },
          ]
        : [
            {
              type: CreatedIssuesTypeEnum.IN_PROGRESS,
              enumValue: CreatedIssuesEnum.IN_PROGRESS,
              labelKey: 'quickFilters.createdIssues.inProgress',
              getCount: (data: CreatedIssuesDto | undefined) =>
                data?.inProgress,
            },
          ]),
    ],
    [isDraftIssuesEnabled, isExtendedWorkflowEnabled, createdIssues],
  );

  return (
    <QuickFilterContainer label={t('quickFilters.createdIssues.title')}>
      {filterConfigs
        .filter((config) => config.condition !== false)
        .map((config) => (
          <QuickFilterItem
            key={config.enumValue}
            label={t(config.labelKey as 'quickFilters.createdIssues.all')}
            number={config.getCount(createdIssues)}
            onClick={() => {
              handleFilterSelection(config.type);
              setActiveFilter(config.enumValue);
            }}
            isActive={activeFilter === config.enumValue}
            testId={config.type}
          />
        ))}
    </QuickFilterContainer>
  );
}
